import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Card, CardContent, Grid,
  CircularProgress, Alert, FormControl, InputLabel,
  Select, Tooltip, Avatar, Badge, Divider,
  List, ListItem, ListItemText, ListItemAvatar,
  Accordion, AccordionSummary, AccordionDetails,
  InputAdornment, Tabs, Tab, Switch,
  FormControlLabel, Rating, Slider,
  ImageList, ImageListItem, ImageListItemBar
} from '@mui/material';
import {
  Edit, Delete, Add, Hotel,
  Search, AcUnit, Air, KingBed,
  Visibility, VisibilityOff, Bed,
  CheckCircle, Cancel, CleaningServices,
  Build, AttachMoney, Schedule,
  People, Image, Star, StarBorder,
  ExpandMore, LocalOffer, Refresh,
  Chair, Wifi, Tv, Restaurant,
  Bathtub, Balcony, RoomService,
  LocationCity, Luggage, MeetingRoom,
  ShoppingCart, EventAvailable, EventBusy,
  TrendingUp, QrCode, Print, Share,
  ArrowForward, ArrowBack, Lock, LockOpen
} from '@mui/icons-material';
import { chambreAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ChambresPage = () => {
  const [chambres, setChambres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPrixRange, setSelectedPrixRange] = useState([0, 1000]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedChambre, setSelectedChambre] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
    pageSize: 10
  });

  // Form data pour création/édition
  const [formData, setFormData] = useState({
    numero: '',
    type_chambre: 'ventilateur',
    description: '',
    capacite: 2,
    prix_nuit: 0,
    prix_heure: 0,
    location_nuit_active: true,
    location_heure_active: true,
    duree_minimum_heures: 2,
    image_principale: null,
    imagePreview: null,
    statut: 'disponible'
  });

  // Form data pour location rapide
  const [locationData, setLocationData] = useState({
    type_location: 'nuit',
    duree: 1,
    nom_client: '',
    telephone_client: '',
    prix_convenu: 0,
    notes: ''
  });

  // Options
  const typeChambreOptions = [
    { value: 'ventilateur', label: 'Ventilateur', icon: <Air />, color: 'info' },
    { value: 'climatiseur', label: 'Climatiseur', icon: <AcUnit />, color: 'primary' },
    { value: 'suite', label: 'Suite', icon: <KingBed />, color: 'warning' },
  ];

  const statutOptions = [
    { value: 'disponible', label: 'Disponible', color: 'success' },
    { value: 'occupee', label: 'Occupée', color: 'error' },
    { value: 'sale', label: 'À nettoyer', color: 'warning' },
    { value: 'maintenance', label: 'En maintenance', color: 'default' },
    { value: 'reservee', label: 'Réservée', color: 'info' },
  ];

  const typeLocationOptions = [
    { value: 'nuit', label: 'Par nuit' },
    { value: 'heure', label: 'Par heure' },
    { value: 'moment', label: 'Moment (forfait)' },
  ];

  const capaciteOptions = [1, 2, 3, 4, 5, 6];

  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    disponibles: 0,
    occupees: 0,
    sales: 0,
    maintenance: 0,
    reservees: 0,
    tauxOccupation: 0,
    revenuMoyen: 0
  });

  // Fetch data
  const fetchChambres = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await chambreAPI.getAll();
      
      let chambresData = [];
      let paginationData = {
        count: 0,
        next: null,
        previous: null
      };
      
      // Gestion des formats de réponse
      if (response.data && Array.isArray(response.data)) {
        chambresData = response.data;
        paginationData.count = response.data.length;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        chambresData = response.data.results;
        paginationData = {
          count: response.data.count || 0,
          next: response.data.next,
          previous: response.data.previous
        };
      } else if (response.pagination) {
        chambresData = response.data;
        paginationData = response.pagination;
      }
      
      setChambres(chambresData);
      setPagination(prev => ({
        ...prev,
        ...paginationData
      }));
      
      calculateStats(chambresData);
      
    } catch (error) {
      console.error('Erreur fetchChambres:', error);
      handleError(error, 'Erreur lors du chargement des chambres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChambres();
  }, []);

  const handleError = (error, defaultMessage) => {
    let errorMessage = defaultMessage;
    
    if (error.response?.data) {
      if (error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    toast.error(errorMessage);
  };

  const calculateStats = (chambresData) => {
    const total = chambresData.length;
    const disponibles = chambresData.filter(c => c.statut === 'disponible').length;
    const occupees = chambresData.filter(c => c.statut === 'occupee').length;
    const sales = chambresData.filter(c => c.statut === 'sale').length;
    const maintenance = chambresData.filter(c => c.statut === 'maintenance').length;
    const reservees = chambresData.filter(c => c.statut === 'reservee').length;
    
    const tauxOccupation = total > 0 ? Math.round((occupees / total) * 100) : 0;
    
    // Calcul du revenu moyen
    const chambresLouables = chambresData.filter(c => c.prix_nuit > 0);
    const revenuMoyen = chambresLouables.length > 0 
      ? chambresLouables.reduce((sum, c) => sum + parseFloat(c.prix_nuit || 0), 0) / chambresLouables.length
      : 0;
    
    setStats({
      total,
      disponibles,
      occupees,
      sales,
      maintenance,
      reservees,
      tauxOccupation,
      revenuMoyen
    });
  };

  const getTypeChambreIcon = (type) => {
    const option = typeChambreOptions.find(t => t.value === type);
    return option ? option.icon : <Hotel />;
  };

  const getTypeChambreLabel = (type) => {
    const option = typeChambreOptions.find(t => t.value === type);
    return option ? option.label : type;
  };

  const getTypeChambreColor = (type) => {
    const option = typeChambreOptions.find(t => t.value === type);
    return option ? option.color : 'default';
  };

  const getStatutColor = (statut) => {
    const option = statutOptions.find(s => s.value === statut);
    return option ? option.color : 'default';
  };

  const getStatutLabel = (statut) => {
    const option = statutOptions.find(s => s.value === statut);
    return option ? option.label : statut;
  };

  const getStatutIcon = (statut) => {
    switch (statut) {
      case 'disponible': return <CheckCircle />;
      case 'occupee': return <Bed />;
      case 'sale': return <CleaningServices />;
      case 'maintenance': return <Build />;
      case 'reservee': return <EventAvailable />;
      default: return <Hotel />;
    }
  };

  // Form handlers
  const resetForm = () => {
    setFormData({
      numero: '',
      type_chambre: 'ventilateur',
      description: '',
      capacite: 2,
      prix_nuit: 0,
      prix_heure: 0,
      location_nuit_active: true,
      location_heure_active: true,
      duree_minimum_heures: 2,
      image_principale: null,
      imagePreview: null,
      statut: 'disponible'
    });
    setIsEditing(false);
  };

  const handleOpenDialog = (chambre = null) => {
    if (chambre) {
      setFormData({
        id: chambre.id,
        numero: chambre.numero || '',
        type_chambre: chambre.type_chambre || 'ventilateur',
        description: chambre.description || '',
        capacite: chambre.capacite || 2,
        prix_nuit: chambre.prix_nuit || 0,
        prix_heure: chambre.prix_heure || 0,
        location_nuit_active: chambre.location_nuit_active !== false,
        location_heure_active: chambre.location_heure_active !== false,
        duree_minimum_heures: chambre.duree_minimum_heures || 2,
        imagePreview: chambre.image_principale,
        statut: chambre.statut || 'disponible'
      });
      setIsEditing(true);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleOpenLocationDialog = (chambre) => {
    setSelectedChambre(chambre);
    setLocationData({
      type_location: 'nuit',
      duree: 1,
      nom_client: '',
      telephone_client: '',
      prix_convenu: chambre.prix_nuit || 0,
      notes: ''
    });
    setOpenLocationDialog(true);
  };

  const handleOpenDetails = (chambre) => {
    setSelectedChambre(chambre);
    setOpenDetailsDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image_principale: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.numero.trim()) {
      toast.error('Le numéro de chambre est requis');
      return;
    }

    if (formData.prix_nuit < 0 || formData.prix_heure < 0) {
      toast.error('Les prix ne peuvent pas être négatifs');
      return;
    }

    if (formData.capacite < 1) {
      toast.error('La capacité doit être au moins 1');
      return;
    }

    setSubmitting(true);

    try {
      const dataToSend = new FormData();
      
      // Ajouter les champs au FormData
      Object.keys(formData).forEach(key => {
        if (key !== 'imagePreview' && key !== 'image_principale') {
          dataToSend.append(key, formData[key]);
        }
      });
      
      // Ajouter l'image si elle existe
      if (formData.image_principale && formData.image_principale instanceof File) {
        dataToSend.append('image_principale', formData.image_principale);
      }

      let response;
      if (isEditing && formData.id) {
        response = await chambreAPI.update(formData.id, dataToSend);
        toast.success('Chambre modifiée avec succès');
      } else {
        response = await chambreAPI.create(dataToSend);
        toast.success('Chambre créée avec succès');
      }

      setOpenDialog(false);
      resetForm();
      fetchChambres();
    } catch (error) {
      handleError(error, 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLocationSubmit = async () => {
    if (!selectedChambre) return;

    if (!locationData.nom_client.trim()) {
      toast.error('Le nom du client est requis');
      return;
    }

    if (locationData.duree < 1) {
      toast.error('La durée doit être au moins 1');
      return;
    }

    setSubmitting(true);

    try {
      // Ici, vous devriez appeler votre API de location
      // Pour l'instant, on simule juste un changement de statut
      await chambreAPI.setStatus(selectedChambre.id, 'occupee');
      
      toast.success('Chambre louée avec succès');
      setOpenLocationDialog(false);
      fetchChambres();
    } catch (error) {
      handleError(error, 'Erreur lors de la location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeStatus = async (id, newStatus) => {
    try {
      await chambreAPI.setStatus(id, newStatus);
      
      let message = '';
      switch (newStatus) {
        case 'disponible': message = 'Chambre marquée comme disponible'; break;
        case 'occupee': message = 'Chambre marquée comme occupée'; break;
        case 'sale': message = 'Chambre marquée comme à nettoyer'; break;
        case 'maintenance': message = 'Chambre mise en maintenance'; break;
        default: message = 'Statut modifié';
      }
      
      toast.success(message);
      fetchChambres();
    } catch (error) {
      handleError(error, 'Erreur lors du changement de statut');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette chambre ?')) {
      try {
        await chambreAPI.delete(id);
        toast.success('Chambre supprimée avec succès');
        fetchChambres();
      } catch (error) {
        handleError(error, 'Erreur lors de la suppression');
      }
    }
  };

  const calculatePrixLocation = () => {
    if (!selectedChambre || !locationData.duree) return 0;
    
    const chambre = selectedChambre;
    const { type_location, duree } = locationData;
    
    if (type_location === 'nuit') {
      return chambre.prix_nuit * duree;
    } else if (type_location === 'heure') {
      let heures = duree;
      if (heures < chambre.duree_minimum_heures) {
        heures = chambre.duree_minimum_heures;
      }
      return chambre.prix_heure * heures;
    } else if (type_location === 'moment') {
      return locationData.prix_convenu;
    }
    
    return 0;
  };

  const getAvailableActions = (chambre) => {
    const actions = [];
    
    if (chambre.statut === 'disponible') {
      actions.push({ 
        label: 'Louer', 
        action: 'louer', 
        color: 'primary', 
        icon: <ShoppingCart /> 
      });
      actions.push({ 
        label: 'Réserver', 
        action: 'reserver', 
        color: 'warning', 
        icon: <EventAvailable /> 
      });
      actions.push({ 
        label: 'Maintenance', 
        action: 'maintenance', 
        color: 'default', 
        icon: <Build /> 
      });
    } else if (chambre.statut === 'occupee') {
      actions.push({ 
        label: 'Libérer', 
        action: 'liberer', 
        color: 'success', 
        icon: <LockOpen /> 
      });
    } else if (chambre.statut === 'sale') {
      actions.push({ 
        label: 'Nettoyer', 
        action: 'nettoyer', 
        color: 'info', 
        icon: <CleaningServices /> 
      });
    } else if (chambre.statut === 'maintenance') {
      actions.push({ 
        label: 'Terminer maintenance', 
        action: 'disponible', 
        color: 'success', 
        icon: <CheckCircle /> 
      });
    } else if (chambre.statut === 'reservee') {
      actions.push({ 
        label: 'Annuler réservation', 
        action: 'disponible', 
        color: 'default', 
        icon: <EventBusy /> 
      });
      actions.push({ 
        label: 'Check-in', 
        action: 'occuper', 
        color: 'primary', 
        icon: <Lock /> 
      });
    }
    
    return actions;
  };

  const handleQuickAction = (chambre, action) => {
    switch (action) {
      case 'louer':
        handleOpenLocationDialog(chambre);
        break;
      case 'reserver':
        handleChangeStatus(chambre.id, 'reservee');
        break;
      case 'liberer':
        handleChangeStatus(chambre.id, 'sale');
        break;
      case 'nettoyer':
        handleChangeStatus(chambre.id, 'disponible');
        break;
      case 'disponible':
        handleChangeStatus(chambre.id, 'disponible');
        break;
      case 'occuper':
        handleChangeStatus(chambre.id, 'occupee');
        break;
      case 'maintenance':
        handleChangeStatus(chambre.id, 'maintenance');
        break;
      default:
        break;
    }
  };

  const filteredChambres = chambres.filter(chambre => {
    const matchesSearch = 
      chambre.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chambre.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    
    const matchesType = selectedType === 'all' || chambre.type_chambre === selectedType;
    const matchesStatus = selectedStatus === 'all' || chambre.statut === selectedStatus;
    const matchesPrice = 
      chambre.prix_nuit >= selectedPrixRange[0] && 
      chambre.prix_nuit <= selectedPrixRange[1];
    
    return matchesSearch && matchesType && matchesStatus && matchesPrice;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <CircularProgress />
        <Typography ml={2}>Chargement des chambres...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Header avec statistiques */}
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <Hotel sx={{ mr: 2, verticalAlign: 'middle' }} />
              Gestion des Chambres
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Gérez les chambres de votre hôtel - location par nuit ou par heure
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Rechercher une chambre..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                sx: { borderRadius: 1 }
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ height: 40, px: 3, borderRadius: 1 }}
            >
              Nouvelle Chambre
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchChambres}
              sx={{ borderRadius: 1 }}
            >
              Actualiser
            </Button>
          </Grid>
        </Grid>

        {/* Cartes statistiques */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Total Chambres
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Disponibles
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.disponibles}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Occupées
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {stats.occupees}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  À nettoyer
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.sales}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Taux d'occupation
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.tauxOccupation}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Prix moyen/nuit
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {stats.revenuMoyen.toFixed(2)} 
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filtres */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Type de chambre</InputLabel>
            <Select
              value={selectedType}
              label="Type de chambre"
              onChange={(e) => setSelectedType(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="all">Tous les types</MenuItem>
              {typeChambreOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {option.icon}
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={selectedStatus}
              label="Statut"
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="all">Tous les statuts</MenuItem>
              {statutOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Chip 
                    label={option.label} 
                    size="small" 
                    color={option.color}
                    variant="outlined"
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ width: 200 }}>
            <Typography variant="caption" color="textSecondary" display="block">
              Prix par nuit: {selectedPrixRange[0]} - {selectedPrixRange[1]} 
            </Typography>
            <Slider
              value={selectedPrixRange}
              onChange={(e, newValue) => setSelectedPrixRange(newValue)}
              valueLabelDisplay="auto"
              min={0}
              max={1000}
              step={10}
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<Hotel />} label="Toutes" />
          <Tab icon={<CheckCircle />} label="Disponibles" />
          <Tab icon={<Bed />} label="Occupées" />
          <Tab icon={<CleaningServices />} label="À nettoyer" />
        </Tabs>
      </Box>

      {/* Vue en grille ou liste */}
      <Box mb={3}>
        <Paper elevation={2} sx={{ borderRadius: 2, p: 2 }}>
          <Grid container spacing={3}>
            {filteredChambres.length > 0 ? (
              filteredChambres.map((chambre) => {
                const availableActions = getAvailableActions(chambre);
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={chambre.id}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6
                        }
                      }}
                    >
                      {/* Image de la chambre */}
                      <Box sx={{ position: 'relative', height: 180 }}>
                        {chambre.image_principale ? (
                          <Avatar
                            src={chambre.image_principale}
                            variant="rounded"
                            sx={{ 
                              width: '100%', 
                              height: '100%',
                              borderRadius: '8px 8px 0 0'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              bgcolor: 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '8px 8px 0 0'
                            }}
                          >
                            <Hotel sx={{ fontSize: 60, color: 'text.disabled' }} />
                          </Box>
                        )}
                        
                        {/* Badge de statut */}
                        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                          <Chip
                            icon={getStatutIcon(chambre.statut)}
                            label={getStatutLabel(chambre.statut)}
                            color={getStatutColor(chambre.statut)}
                            size="small"
                            sx={{ borderRadius: 1 }}
                          />
                        </Box>
                        
                        {/* Badge de type */}
                        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                          <Chip
                            icon={getTypeChambreIcon(chambre.type_chambre)}
                            label={getTypeChambreLabel(chambre.type_chambre)}
                            color={getTypeChambreColor(chambre.type_chambre)}
                            size="small"
                            sx={{ borderRadius: 1 }}
                          />
                        </Box>
                      </Box>
                      
                      <CardContent>
                        {/* Numéro et capacité */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="h5" fontWeight="bold">
                            Chambre {chambre.numero}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <People fontSize="small" />
                            <Typography variant="body2">
                              {chambre.capacite} pers.
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Description */}
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ mb: 2 }}>
                          {chambre.description || 'Aucune description'}
                        </Typography>
                        
                        {/* Prix */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Box>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Par nuit
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {parseFloat(chambre.prix_nuit || 0).toFixed(2)} 
                            </Typography>
                          </Box>
                          
                          {chambre.prix_heure > 0 && (
                            <Box textAlign="right">
                              <Typography variant="caption" color="textSecondary" display="block">
                                Par heure
                              </Typography>
                              <Typography variant="body1">
                                {parseFloat(chambre.prix_heure || 0).toFixed(2)} 
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        
                        {/* Actions rapides */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                          <Box display="flex" gap={0.5}>
                            {availableActions.map((action, index) => (
                              <Tooltip key={index} title={action.label}>
                                <IconButton
                                  color={action.color}
                                  size="small"
                                  onClick={() => handleQuickAction(chambre, action.action)}
                                >
                                  {action.icon}
                                </IconButton>
                              </Tooltip>
                            ))}
                          </Box>
                          
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Détails">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDetails(chambre)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(chambre)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(chambre.id)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
                  {/* <Hotel sx={{ fontSize: 60, color: 'text.disabled' }} /> */}
                  <Typography variant="body1" color="textSecondary">
                    Aucune chambre trouvée
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>

      {/* Dialog de création/édition */}
      <Dialog 
        open={openDialog} 
        onClose={() => !submitting && setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Hotel />
            <Typography variant="h6">
              {isEditing ? 'Modifier la Chambre' : 'Nouvelle Chambre'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Numéro de chambre"
                  fullWidth
                  value={formData.numero}
                  onChange={(e) => setFormData({...formData, numero: e.target.value})}
                  required
                  error={!formData.numero.trim()}
                  helperText={!formData.numero.trim() ? 'Ce champ est requis' : 'Ex: 101, 102, 201...'}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type de chambre</InputLabel>
                  <Select
                    value={formData.type_chambre}
                    label="Type de chambre"
                    onChange={(e) => setFormData({...formData, type_chambre: e.target.value})}
                    disabled={submitting}
                  >
                    {typeChambreOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {option.icon}
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Capacité (personnes)</InputLabel>
                  <Select
                    value={formData.capacite}
                    label="Capacité (personnes)"
                    onChange={(e) => setFormData({...formData, capacite: e.target.value})}
                    disabled={submitting}
                  >
                    {capaciteOptions.map((capacity) => (
                      <MenuItem key={capacity} value={capacity}>
                        {capacity} personne{capacity > 1 ? 's' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.statut}
                    label="Statut"
                    onChange={(e) => setFormData({...formData, statut: e.target.value})}
                    disabled={submitting}
                  >
                    {statutOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Chip 
                          label={option.label} 
                          size="small" 
                          color={option.color}
                          variant="outlined"
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="Prix par nuit"
                  type="number"
                  fullWidth
                  value={formData.prix_nuit}
                  onChange={(e) => setFormData({...formData, prix_nuit: parseFloat(e.target.value) || 0})}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"></InputAdornment>,
                    inputProps: { step: "0.01", min: "0" }
                  }}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="Prix par heure"
                  type="number"
                  fullWidth
                  value={formData.prix_heure}
                  onChange={(e) => setFormData({...formData, prix_heure: parseFloat(e.target.value) || 0})}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"></InputAdornment>,
                    inputProps: { step: "0.01", min: "0" }
                  }}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="Durée minimum (heures)"
                  type="number"
                  fullWidth
                  value={formData.duree_minimum_heures}
                  onChange={(e) => setFormData({...formData, duree_minimum_heures: parseInt(e.target.value) || 2})}
                  InputProps={{ inputProps: { min: "1" } }}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                  helperText="Durée minimum pour location à l'heure"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  multiline
                  rows={3}
                  fullWidth
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description de la chambre, équipements, vue..."
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box display="flex" gap={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.location_nuit_active}
                        onChange={(e) => setFormData({...formData, location_nuit_active: e.target.checked})}
                        disabled={submitting}
                      />
                    }
                    label="Location par nuit activée"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.location_heure_active}
                        onChange={(e) => setFormData({...formData, location_heure_active: e.target.checked})}
                        disabled={submitting}
                      />
                    }
                    label="Location par heure activée"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Image de la chambre
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<Image />}
                      disabled={submitting}
                      sx={{ borderRadius: 1 }}
                    >
                      Choisir une image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={submitting}
                      />
                    </Button>
                    {formData.imagePreview && (
                      <Avatar
                        src={formData.imagePreview}
                        variant="rounded"
                        sx={{ 
                          width: 100, 
                          height: 100,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => !submitting && setOpenDialog(false)} 
            variant="outlined"
            disabled={submitting}
            sx={{ borderRadius: 1 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.numero.trim() || formData.prix_nuit < 0 || formData.prix_heure < 0}
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Edit /> : <Add />)}
            sx={{ borderRadius: 1 }}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de location rapide */}
      {selectedChambre && (
        <Dialog
          open={openLocationDialog}
          onClose={() => !submitting && setOpenLocationDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <ShoppingCart />
              <Typography variant="h6">
                Louer la chambre {selectedChambre.numero}
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar
                      src={selectedChambre.image_principale}
                      variant="rounded"
                      sx={{ width: 80, height: 80 }}
                    >
                      {!selectedChambre.image_principale && <Hotel />}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        Chambre {selectedChambre.numero}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {getTypeChambreLabel(selectedChambre.type_chambre)} • {selectedChambre.capacite} pers.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type de location</InputLabel>
                    <Select
                      value={locationData.type_location}
                      label="Type de location"
                      onChange={(e) => {
                        const newType = e.target.value;
                        setLocationData({
                          ...locationData,
                          type_location: newType,
                          prix_convenu: newType === 'moment' ? selectedChambre.prix_nuit : locationData.prix_convenu
                        });
                      }}
                      disabled={submitting}
                    >
                      {typeLocationOptions.map((option) => (
                        <MenuItem 
                          key={option.value} 
                          value={option.value}
                          disabled={
                            (option.value === 'nuit' && !selectedChambre.location_nuit_active) ||
                            (option.value === 'heure' && !selectedChambre.location_heure_active)
                          }
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label={
                      locationData.type_location === 'nuit' ? 'Nombre de nuits' :
                      locationData.type_location === 'heure' ? 'Nombre d\'heures' :
                      'Durée (heures)'
                    }
                    type="number"
                    fullWidth
                    value={locationData.duree}
                    onChange={(e) => setLocationData({...locationData, duree: parseInt(e.target.value) || 1})}
                    InputProps={{ inputProps: { min: "1" } }}
                    disabled={submitting}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Nom du client"
                    fullWidth
                    value={locationData.nom_client}
                    onChange={(e) => setLocationData({...locationData, nom_client: e.target.value})}
                    required
                    disabled={submitting}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Téléphone du client"
                    fullWidth
                    value={locationData.telephone_client}
                    onChange={(e) => setLocationData({...locationData, telephone_client: e.target.value})}
                    disabled={submitting}
                  />
                </Grid>
                
                {locationData.type_location === 'moment' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Prix convenu (forfait)"
                      type="number"
                      fullWidth
                      value={locationData.prix_convenu}
                      onChange={(e) => setLocationData({...locationData, prix_convenu: parseFloat(e.target.value) || 0})}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"></InputAdornment>,
                        inputProps: { step: "0.01", min: "0" }
                      }}
                      disabled={submitting}
                    />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    multiline
                    rows={2}
                    fullWidth
                    value={locationData.notes}
                    onChange={(e) => setLocationData({...locationData, notes: e.target.value})}
                    placeholder="Instructions spéciales, heure d'arrivée..."
                    disabled={submitting}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h4" color="primary">
                      {calculatePrixLocation().toFixed(2)} 
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
            <Button
              onClick={() => setOpenLocationDialog(false)}
              variant="outlined"
              disabled={submitting}
              sx={{ borderRadius: 1 }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleLocationSubmit}
              variant="contained"
              disabled={submitting || !locationData.nom_client.trim()}
              startIcon={submitting ? <CircularProgress size={20} /> : <ShoppingCart />}
              sx={{ borderRadius: 1 }}
            >
              {submitting ? 'Enregistrement...' : 'Louer la chambre'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog de détails */}
      {selectedChambre && (
        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Hotel />
              <Typography variant="h6">
                Détails de la chambre {selectedChambre.numero}
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', height: 300 }}>
                  {selectedChambre.image_principale ? (
                    <Avatar
                      src={selectedChambre.image_principale}
                      variant="rounded"
                      sx={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Hotel sx={{ fontSize: 100, color: 'text.disabled' }} />
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      Chambre {selectedChambre.numero}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Chip
                        icon={getTypeChambreIcon(selectedChambre.type_chambre)}
                        label={getTypeChambreLabel(selectedChambre.type_chambre)}
                        color={getTypeChambreColor(selectedChambre.type_chambre)}
                      />
                      <Chip
                        icon={getStatutIcon(selectedChambre.statut)}
                        label={getStatutLabel(selectedChambre.statut)}
                        color={getStatutColor(selectedChambre.statut)}
                      />
                    </Box>
                  </Box>
                  
                  <Box textAlign="right">
                    <Typography variant="h4" color="primary">
                      {parseFloat(selectedChambre.prix_nuit || 0).toFixed(2)} 
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      par nuit
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={3} mb={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <People />
                    <Typography>
                      {selectedChambre.capacite} personne{selectedChambre.capacite > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrendingUp />
                    <Typography>
                      {selectedChambre.nombre_locations || 0} location{selectedChambre.nombre_locations !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" gutterBottom>Description</Typography>
                <Typography variant="body1" paragraph>
                  {selectedChambre.description || 'Aucune description disponible.'}
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Tarifs</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Par nuit:</Typography>
                    <Typography variant="body1">
                      {parseFloat(selectedChambre.prix_nuit || 0).toFixed(2)} 
                    </Typography>
                  </Grid>
                  
                  {selectedChambre.prix_heure > 0 && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Par heure:</Typography>
                      <Typography variant="body1">
                        {parseFloat(selectedChambre.prix_heure || 0).toFixed(2)} 
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        (min. {selectedChambre.duree_minimum_heures}h)
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Options de location</Typography>
                <Box display="flex" gap={2}>
                  {selectedChambre.location_nuit_active && (
                    <Chip label="Location par nuit" color="success" variant="outlined" />
                  )}
                  {selectedChambre.location_heure_active && (
                    <Chip label="Location par heure" color="info" variant="outlined" />
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
            <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">
              Fermer
            </Button>
            {selectedChambre.statut === 'disponible' && (
              <Button
                variant="contained"
                startIcon={<ShoppingCart />}
                onClick={() => {
                  setOpenDetailsDialog(false);
                  handleOpenLocationDialog(selectedChambre);
                }}
              >
                Louer cette chambre
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ChambresPage;